function asRows(value) {
  return Array.isArray(value) ? value : [];
}

/**
 * Reconstruct the charge lines represented by a completed checkout.
 *
 * Most checkout charges are linked directly to the finalized visit. Some
 * legitimate collections, including an Ortho Installation Day down payment
 * and prior AR, are intentionally anchored outside that visit. Those lines
 * belong on this receipt only to the extent that this checkout's payment was
 * explicitly allocated to them.
 */
export function completedReceiptChargeEvents({
  directEvents = [],
  allocations = [],
  allocatedTargetEvents = [],
} = {}) {
  const directCharges = asRows(directEvents)
    .filter((event) => event?.event_type === "charge");
  const directChargeIds = new Set(directCharges.map((event) => event?.id).filter(Boolean));
  const allocatedByTargetId = new Map();

  for (const allocation of asRows(allocations)) {
    const targetId = allocation?.allocated_to_event_id;
    const amountCentavos = Number(allocation?.amount_centavos || 0);
    if (!targetId || !Number.isFinite(amountCentavos) || amountCentavos <= 0) continue;
    allocatedByTargetId.set(
      targetId,
      (allocatedByTargetId.get(targetId) || 0) + Math.round(amountCentavos),
    );
  }

  const externalCharges = asRows(allocatedTargetEvents)
    .filter((event) => (
      event?.id
      && event?.event_type === "charge"
      && !event?.reverses_event_id
      && event?.document_voided !== true
      && !directChargeIds.has(event.id)
      && allocatedByTargetId.has(event.id)
    ))
    .map((event) => ({
      ...event,
      // A partial prior-AR collection must show only what this receipt paid,
      // not the target charge's full historical balance.
      amount_centavos: allocatedByTargetId.get(event.id),
      metadata: {
        ...(event?.metadata && typeof event.metadata === "object" ? event.metadata : {}),
        receipt_allocation_only: true,
        receipt_allocated_amount_centavos: allocatedByTargetId.get(event.id),
      },
    }));

  return [...directCharges, ...externalCharges];
}
